import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;

function fmtDate(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleString(); } catch { return '—'; }
}

export default function PromoWorldApiSettings({ instanceId }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('PromoWorld Website API');
  const [rawKey, setRawKey] = useState('');
  const [rawKeyDialogOpen, setRawKeyDialogOpen] = useState(false);
  const [error, setError] = useState('');

  const headers = { accessToken: localStorage.getItem('accessToken') };

  const loadKeys = async () => {
    if (!instanceId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await axios.get(`${SERVER}/promoworld/api-keys`, {
        params: { instanceId },
        headers,
      });
      setKeys(resp.data?.keys || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load PromoWorld API keys.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadKeys(); }, [instanceId]);

  const showOneTimeKey = (nextRawKey) => {
    setRawKey(nextRawKey || '');
    setRawKeyDialogOpen(true);
  };

  const generateKey = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${SERVER}/promoworld/api-keys`, { instanceId, name }, { headers });
      showOneTimeKey(resp.data?.rawKey);
      await loadKeys();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to generate PromoWorld API key.');
    } finally {
      setLoading(false);
    }
  };

  const rotateKey = async (key) => {
    if (!window.confirm(`Rotate API key ${key.prefix}? The old key will stop working.`)) return;
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${SERVER}/promoworld/api-keys/${key.id}/rotate`, { instanceId }, { headers });
      showOneTimeKey(resp.data?.rawKey);
      await loadKeys();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to rotate PromoWorld API key.');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (key) => {
    if (!window.confirm(`Revoke API key ${key.prefix}? Websites using it will lose access.`)) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${SERVER}/promoworld/api-keys/${key.id}/revoke`, { instanceId }, { headers });
      await loadKeys();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to revoke PromoWorld API key.');
    } finally {
      setLoading(false);
    }
  };

  const copyRawKey = async () => {
    try { await navigator.clipboard.writeText(rawKey); } catch {}
  };

  const curlExample = `curl -H "x-api-key: ${rawKey || '<your_key>'}" \\\n  "${SERVER}/api/v1/products?q=hoodie&page=1&rpp=24"`;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <VpnKeyOutlinedIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>PromoWorld API</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Generate per-instance API keys for external websites to read the global PromoWorld catalogue.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              label="Key name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
            <Button variant="contained" onClick={generateKey} disabled={loading || !instanceId}>
              Generate API Key
            </Button>
          </Stack>
        </Stack>

        {!instanceId && <Alert severity="info" sx={{ mt: 2 }}>Instance is still loading.</Alert>}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Alert severity="info" sx={{ mt: 2 }}>
          The internal PromoWorld tab continues to use Core authentication. These keys are only for external `/api/v1/products` access.
        </Alert>

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Prefix</TableCell>
                <TableCell>Last 4</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name || 'PromoWorld API Key'}</TableCell>
                  <TableCell><code>{key.prefix}</code></TableCell>
                  <TableCell>{key.lastFour ? <code>••••{key.lastFour}</code> : "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" color={key.isActive ? 'success' : 'default'} label={key.isActive ? 'Active' : 'Revoked'} />
                  </TableCell>
                  <TableCell>{fmtDate(key.lastUsedAt)}</TableCell>
                  <TableCell>{fmtDate(key.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" disabled={loading || !key.isActive} onClick={() => rotateKey(key)}>Rotate</Button>
                      <Button size="small" color="error" variant="outlined" disabled={loading || !key.isActive} onClick={() => revokeKey(key)}>Revoke</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!keys.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      No PromoWorld API keys have been generated for this instance yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Dialog open={rawKeyDialogOpen} onClose={() => setRawKeyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Copy your PromoWorld API key</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this key now. You will not be able to see it again.
          </Alert>
          <TextField
            value={rawKey}
            fullWidth
            label="Raw API key"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Tooltip title="Copy">
                  <IconButton onClick={copyRawKey} edge="end"><ContentCopyIcon /></IconButton>
                </Tooltip>
              ),
            }}
          />
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Example usage</Typography>
          <Box component="pre" sx={{ p: 1.5, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 1, overflowX: 'auto', fontSize: 12 }}>
            {curlExample}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRawKeyDialogOpen(false)}>Done</Button>
          <Button variant="contained" onClick={copyRawKey}>Copy Key</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
